/**
 * Vertrag der Medienbibliothek (Stufe G): Liste je Ordner, neueste zuerst, mit Cursor und Zählern (G.1) sowie
 * Hochladen über einen privaten Staging-Bucket mit Verarbeitung (G.2, `apps/app/docs/STUFE_G_PLAN.md` §2/§4).
 * `uploadable` ist nur wahr, wenn die Stage Uploads anbietet und der Mandant sie nutzen darf.
 */
import { z } from 'zod';
import { IsoDateTime, MediaId, MediaUploadError, MediaUploadState, mediaLimits } from '@pat/core/entities';
import { MediaView } from './content.js';

/** Ordner im Hosting (`<hostingDir>/p/…`, `<hostingDir>/u/…`): `p` = Artikelbilder, `u` = Design-Bilder (Logo, Hintergründe). */
export const MediaFolder = z.enum(['p', 'u']);
export type MediaFolder = z.infer<typeof MediaFolder>;
/** Auswahl in der Bibliothek: `all` = beide Ordner. */
export const MediaFolderChoice = z.enum(['all', 'p', 'u']);
export type MediaFolderChoice = z.infer<typeof MediaFolderChoice>;

export const MEDIA_PAGE_DEFAULT = 24;
/** Obergrenze je Seite (Port `listMedia`, höchstens zwei Lesevorgänge je Seite). */
export const MEDIA_PAGE_MAX = 48;

export const MediaLibraryQuery = z
  .object({
    folder: MediaFolderChoice.default('all'),
    cursor: z.string().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(MEDIA_PAGE_MAX).default(MEDIA_PAGE_DEFAULT),
  })
  .strict();
export type MediaLibraryQuery = z.infer<typeof MediaLibraryQuery>;

/** Eintrag der Bibliothek; `name` (Anzeigename) gibt es erst mit dem Hochladen bzw. dem Import. */
export const MediaListItem = MediaView.extend({
  folder: MediaFolder,
  name: z.string().max(255).optional(),
  bytes: z.number().int().nonnegative().optional(),
  createdAt: IsoDateTime,
});
export type MediaListItem = z.infer<typeof MediaListItem>;

/** `complete: false` = Katalog noch nicht vollständig abgeglichen (core `MediaStat.catalogComplete`): keine Zahlen. */
export const MediaCounts = z.discriminatedUnion('complete', [
  z.object({ complete: z.literal(true), p: z.number().int().nonnegative(), u: z.number().int().nonnegative() }).strict(),
  z.object({ complete: z.literal(false) }).strict(),
]);
export type MediaCounts = z.infer<typeof MediaCounts>;

export const MediaListResponse = z.object({
  media: z.array(MediaListItem),
  nextCursor: z.string().max(2048).optional(),
  counts: MediaCounts,
  /** Basis der öffentlichen Adressen (wie `PreviewContext.hostingBaseUrl`). */
  hostingBaseUrl: z.string().url(),
  editable: z.boolean(),
  /** Hochladen möglich: Stage bietet Uploads an, Mandant migriert mit `hostingDir`, Rolle darf bearbeiten. */
  uploadable: z.boolean(),
});
export type MediaListResponse = z.infer<typeof MediaListResponse>;

// ---------------------------------------------------------------------------
// Hochladen (G.2). Ablauf: `POST /media/uploads` → Formular-Upload direkt in den Staging-Bucket (presigned POST)
// → Verarbeitung (Codex) → `GET /media/uploads?ids=` bis `ready`/`rejected`/`expired`.

/** Erlaubte Typen wie core `MediaUpload.declaredType` (SVG, HTML, PDF, HEIC nie). */
export const UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;
export const UploadType = z.enum(UPLOAD_TYPES);
export type UploadType = z.infer<typeof UploadType>;
/** Grenzen aus core (`mediaLimits`, Standardwerte 24.10.2026). Die SPA prüft vorab nur für die Bedienung. */
export const MAX_UPLOAD_BYTES = mediaLimits.maxInputBytes;
export const MAX_PARALLEL_UPLOADS = 3;
/** Höchstzahl je Statusabfrage. */
export const UPLOAD_STATUS_MAX = 20;

/** Anlegen eines Uploads; `requestId` je Datei aus der SPA (Wiederholung nach unklarem Ausgang ist idempotent). */
export const MediaUploadRequest = z
  .object({
    requestId: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
    folder: MediaFolder,
    fileName: z.string().trim().min(1).max(255),
    contentType: UploadType,
    bytes: z.number().int().min(1).max(MAX_UPLOAD_BYTES),
  })
  .strict();
export type MediaUploadRequest = z.infer<typeof MediaUploadRequest>;

/** Presigned POST: Formularfelder unverändert mitsenden, die Datei als letztes Feld `file`. */
export const MediaUploadTicket = z.object({
  uploadId: z.string().min(1).max(64),
  mediaId: MediaId,
  url: z.string().url(),
  fields: z.record(z.string(), z.string()),
  expiresAt: IsoDateTime,
});
export type MediaUploadTicket = z.infer<typeof MediaUploadTicket>;

export { MediaUploadError, MediaUploadState };

/** Stand eines Uploads; `media` erst bei `ready`, `code` bei `rejected`. */
export const MediaUploadStatus = z.object({
  uploadId: z.string().min(1).max(64),
  state: MediaUploadState,
  media: MediaListItem.optional(),
  code: MediaUploadError.optional(),
});
export type MediaUploadStatus = z.infer<typeof MediaUploadStatus>;

export const MediaUploadStatusQuery = z
  .object({
    ids: z
      .string()
      .min(1)
      .max(UPLOAD_STATUS_MAX * 65)
      .transform(value => value.split(','))
      .pipe(z.array(z.string().regex(/^[A-Za-z0-9_-]{1,64}$/)).min(1).max(UPLOAD_STATUS_MAX)),
  })
  .strict();
export type MediaUploadStatusQuery = z.infer<typeof MediaUploadStatusQuery>;

/** Unbekannte oder fremde IDs fehlen in der Antwort (kein Hinweis auf andere Mandanten). */
export const MediaUploadStatusResponse = z.object({ uploads: z.array(MediaUploadStatus) });
export type MediaUploadStatusResponse = z.infer<typeof MediaUploadStatusResponse>;

/** Öffentliche Adresse eines Mediums wie `mediaUrl` im Renderer: Ursprung der Hostingbasis + `objectKey`. */
export function mediaPublicUrl(hostingBaseUrl: string, objectKey: string): string {
  return `${new URL(hostingBaseUrl).origin}/${objectKey.replace(/^\/+/, '')}`;
}

/** Anzeigename: `name`, sonst der Dateiname aus dem Schlüssel. */
export function mediaName(media: Pick<MediaListItem, 'objectKey' | 'name'>): string {
  return media.name ?? media.objectKey.split('/').pop() ?? media.objectKey;
}
